import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { ShoppingCart, Clock, Trash2, RotateCcw } from 'lucide-react'

interface CartItem {
  id: string
  name: string
  price: number
  quantity: number
  total: number
  gst_rate: number
  gst_type: 'inclusive' | 'exclusive'
}

interface SavedCartData {
  cart: CartItem[]
  selectedCustomer?: any
  paymentMethod?: string
  amountReceived?: string
  splitPayments?: any
  orderDiscount?: any
  timestamp?: number
}

interface CartRestorationModalProps {
  isOpen: boolean
  onClose: () => void
  savedCartData: SavedCartData | null
  onRestore: () => void
  onSuspendAndNew: () => void
  onDiscardAndNew: () => void
  formatCurrency: (amount: number) => string
}

export const CartRestorationModal = ({
  isOpen,
  onClose,
  savedCartData,
  onRestore,
  onSuspendAndNew,
  onDiscardAndNew,
  formatCurrency
}: CartRestorationModalProps) => {
  console.log('CartRestorationModal render:', { isOpen, savedCartData })
  if (!isOpen || !savedCartData) return null

  const cartTotal = savedCartData.cart.reduce((sum, item) => sum + item.total, 0)
  const itemCount = savedCartData.cart.length
  const totalQuantity = savedCartData.cart.reduce((sum, item) => sum + item.quantity, 0)

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
      <Card className="w-full max-w-md sm:max-w-2xl bg-white dark:bg-gray-800 shadow-2xl border-0 max-h-[90vh] overflow-y-auto">
        <div className="p-3 sm:p-6">
          {/* Header */}
          <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center">
              <ShoppingCart className="h-4 w-4 sm:h-6 sm:w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <h3 className="text-base sm:text-xl font-semibold text-gray-900 dark:text-gray-100">
                Previous Cart Found
              </h3>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                You have an unsaved cart from your previous session
              </p>
            </div>
          </div>

          {/* Cart Summary */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
            <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center">
              <div>
                <div className="text-lg sm:text-2xl font-bold text-orange-600 dark:text-orange-400">
                  {itemCount}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {itemCount === 1 ? 'Item' : 'Items'}
                </div>
              </div>
              <div>
                <div className="text-lg sm:text-2xl font-bold text-orange-600 dark:text-orange-400">
                  {totalQuantity}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Quantity</div>
              </div>
              <div>
                <div className="text-lg sm:text-2xl font-bold text-orange-600 dark:text-orange-400">
                  {formatCurrency(cartTotal)}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Total Value</div>
              </div>
            </div>
          </div>

          {/* Cart Items Preview */}
          <div className="max-h-32 sm:max-h-40 overflow-y-auto mb-4 sm:mb-6 border border-gray-200 dark:border-gray-600 rounded-lg">
            <div className="p-2 sm:p-3 space-y-1 sm:space-y-2">
              {savedCartData.cart.slice(0, 5).map((item) => (
                <div key={item.id} className="flex justify-between items-center text-xs sm:text-sm">
                  <div className="flex-1 min-w-0">
                    <span className="text-gray-900 dark:text-gray-100 font-medium truncate">
                      {item.name}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 ml-1 sm:ml-2">
                      × {item.quantity}
                    </span>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300 font-medium text-xs sm:text-sm">
                    {formatCurrency(item.total)}
                  </span>
                </div>
              ))}
              {savedCartData.cart.length > 5 && (
                <div className="text-xs text-gray-500 dark:text-gray-400 text-center py-1">
                  +{savedCartData.cart.length - 5} more items...
                </div>
              )}
            </div>
          </div>

          {/* Action Options */}
          <div className="space-y-2 sm:space-y-3">
            <div className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 sm:mb-3">
              What would you like to do?
            </div>
            
            {/* Restore Cart */}
            <Button
              onClick={onRestore}
              className="w-full justify-start h-10 sm:h-12 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white border-0"
            >
              <RotateCcw className="h-3 w-3 sm:h-4 sm:w-4 mr-2 sm:mr-3" />
              <div className="text-left">
                <div className="text-sm sm:text-base font-medium">Continue Previous Cart</div>
                <div className="text-xs opacity-90 hidden sm:block">Resume where you left off</div>
              </div>
            </Button>

            {/* Suspend and Start New */}
            <Button
              onClick={onSuspendAndNew}
              variant="outline"
              className="w-full justify-start h-10 sm:h-12 border-orange-200 dark:border-orange-700 hover:bg-orange-50 dark:hover:bg-orange-900/20"
            >
              <Clock className="h-3 w-3 sm:h-4 sm:w-4 mr-2 sm:mr-3 text-orange-600 dark:text-orange-400" />
              <div className="text-left">
                <div className="text-sm sm:text-base font-medium text-gray-900 dark:text-gray-100">
                  Save to Suspended Sales & Start New
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">
                  Keep current cart safe and start fresh
                </div>
              </div>
            </Button>

            {/* Discard and Start New */}
            <Button
              onClick={onDiscardAndNew}
              variant="outline" 
              className="w-full justify-start h-10 sm:h-12 border-red-200 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <Trash2 className="h-3 w-3 sm:h-4 sm:w-4 mr-2 sm:mr-3 text-red-600 dark:text-red-400" />
              <div className="text-left">
                <div className="text-sm sm:text-base font-medium text-gray-900 dark:text-gray-100">
                  Discard Previous Cart & Start New
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">
                  Permanently delete the previous cart
                </div>
              </div>
            </Button>
          </div>

          {/* Footer */}
          <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-600">
            <div className="flex justify-end">
              <Button
                onClick={onClose}
                variant="ghost"
                size="sm"
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-xs sm:text-sm"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}